import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {PortalUsageApi} from '../../../api/portalUsageApi';
import {Spinner} from '../../Spinner';
import {PortalUsageEmptyState} from '../../portal_usage/PortalUsageEmptyState';
import {
    countByState,
    filterClients,
    sortClients,
    USAGE_FILTER,
    USAGE_SORT,
    USAGE_STATE,
} from '../../portal_usage/portalUsageState';
import {AgencyTabs} from '../AgencyTabs';
import {AgencyVoiceSettings} from '../AgencyVoiceSettings';
import {PortalUsageFunnel} from './PortalUsageFunnel';
import {PortalUsageRow} from './PortalUsageRow';
import {PortalUsageToolbar} from './PortalUsageToolbar';

/**
 * "Portal de clientes" — la adopción del portal y del asistente en TODOS los clientes.
 *
 * Contesta las dos preguntas que la agencia se hace después de mirar un cliente suelto: a quién
 * invité y nunca entró, y quién dejó de entrar. Por eso los clientes en cero viajan en la
 * respuesta y se listan igual: la lista de los que **no** adoptaron es la accionable.
 *
 * Una sola llamada arma la pantalla entera, y el backend la resuelve con un número FIJO de
 * queries: con 3 clientes y con 400 son las mismas. Todo el filtrado, la búsqueda y el orden son
 * client-side sobre ese payload — mismo criterio que el Listado de Clientes v2.
 */
export function AgencyPortalUsageContent() {
    const {t} = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState(USAGE_FILTER.ALL);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState(USAGE_SORT.ATTENTION);

    const fetchOverview = useCallback(() => {
        setLoading(true);
        setFailed(false);
        return PortalUsageApi.getAgencyOverview().then((response) => {
            if (response && response.status === 'OK' && response.data) {
                setData(response.data);
            } else {
                setData(null);
                setFailed(true);
            }
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    // El `|| []` va DENTRO de un memo: como expresión suelta devuelve un array nuevo en cada
    // render y anula los dos memos de abajo, que es justo lo que sostiene que ordenar 400 filas no
    // se recalcule al teclear en el buscador.
    const clients = useMemo(() => (data && data.clients) || [], [data]);
    // `undefined` mientras el backend viejo no lo mande: `clientReportsGap` sólo afirma que el plan
    // bloquea con un `false` explícito, así que durante el skew nadie se marca por esta vía.
    const agencyReportsModule = data ? data.agency_reports_module : undefined;
    const rows = useMemo(
        () => sortClients(
            filterClients(clients, {state: filter, search, agencyReportsModule}),
            sort, {agencyReportsModule}),
        [clients, filter, search, sort, agencyReportsModule],
    );
    const moduleOffCount = useMemo(
        () => countByState(clients)[USAGE_STATE.MODULE_OFF] || 0,
        [clients],
    );
    // El número del chip lo define el BACKEND (`totals.clients_reports_blocked`), no un recuento
    // propio: es el mismo KPI que la agencia puede ver en otra superficie, y dos definiciones
    // divergen sin que nadie lo note. El predicado por fila (`clientReportsGap`) sigue existiendo,
    // pero sólo para decidir a quién marcar y a quién filtrar — nunca para producir el total.
    // Sin `totals` (backend viejo) el chip no aparece, que es lo correcto durante el skew.
    const blockedCount = (data && data.totals && data.totals.clients_reports_blocked) || 0;

    // El ranking es lo ÚNICO que depende del overview. Se calcula aparte para que su carga y su
    // error vivan dentro de la pantalla y no como early-return de toda ella: la voz de la agencia
    // no tiene nada que ver con este endpoint y no puede dejar de existir cuando falla.
    const renderRank = () => {
        if (loading && !data) {
            return (
                <div className="pu-rank">
                    <div className="box-loader"><Spinner color="positive"/></div>
                </div>
            );
        }

        if (failed || !data) {
            return (
                <div className="pu-rank">
                    <PortalUsageEmptyState kind="ERROR" onRetry={fetchOverview}/>
                </div>
            );
        }

        return (
            <div className="pu-rank">
            <PortalUsageFunnel
                funnel={data.funnel || {}}
                totals={data.totals}
                windowDays={data.window_days}
                moduleOffCount={moduleOffCount}
                blockedCount={blockedCount}
                active={filter}
                onSelect={setFilter}
            />

            {/* El plan es una condición de la AGENCIA, igual para las 72 filas: repetirla en cada
                una sería ruido y taparía el aviso por cliente, que sí es específico. Sólo aparece
                si además hay informes compartidos — sin nada compartido no hay nada bloqueado. */}
            {agencyReportsModule === false && (data.totals || {}).shared_reports > 0 ? (
                <p className="pu-rank__banner">
                    {t('portal_usage.rank_plan_blocked',
                        'Tu plan no incluye el Explorador de datos, así que los informes que '
                        + 'compartes no se ven en el portal de ningún cliente.')}{' '}
                    <Link to="/agency/subscription">
                        {t('portal_usage.rank_plan_blocked_cta', 'Ver mi suscripción')}
                    </Link>
                </p>
            ) : null}

            {clients.length === 0 ? (
                <PortalUsageEmptyState kind="NO_USERS"/>
            ) : (
                <>
                    <PortalUsageToolbar
                        search={search}
                        onSearch={setSearch}
                        sort={sort}
                        onSort={setSort}
                        shown={rows.length}
                        total={clients.length}
                    />

                    {/* «Usuarios» salió del grid: su número ya vive en la sub-línea del nombre desde
                        el primer día, y sostener dos veces el mismo dato costaba la columna que
                        necesitan los informes. */}
                    <div className="pu-rank__head-row" aria-hidden="true">
                        <span>{t('portal_usage.rank_col_client', 'Cliente')}</span>
                        <span>{t('portal_usage.rank_col_state', 'Estado')}</span>
                        <span className="-num">
                            <i className="pu-dot -chat"/>
                            {t('portal_usage.rank_col_queries', 'Consultas al chat')}
                        </span>
                        <span className="-num">
                            <i className="pu-dot -rep"/>
                            {t('portal_usage.rank_col_reports', 'Ingresos a informes')}
                        </span>
                        <span className="-num">{t('portal_usage.rank_col_shared', 'Compartidos')}</span>
                        <span>{t('portal_usage.rank_col_activity', 'Actividad')}</span>
                        <span>{t('portal_usage.rank_col_last', 'Última')}</span>
                    </div>

                    <div className="pu-rank__list">
                        {rows.map(client => (
                            <PortalUsageRow
                                key={client.client_id}
                                client={client}
                                agencyReportsModule={agencyReportsModule}
                            />
                        ))}
                    </div>

                    {/* Un filtro que no deja nada NO es un vacío de la pantalla: es un filtro que
                        hay que poder soltar sin adivinar cómo. */}
                    {rows.length === 0 ? (
                        <p className="pu-rank__nothing">
                            {t('portal_usage.rank_nothing', 'Ningún cliente coincide con el filtro.')}{' '}
                            <button className="pu-rank__clear" type="button" onClick={() => {
                                setFilter(USAGE_FILTER.ALL);
                                setSearch('');
                            }}>
                                {t('portal_usage.rank_clear', 'Ver todos')}
                            </button>
                        </p>
                    ) : null}
                </>
            )}
            </div>
        );
    };

    return (
        <section className="section-content">
            {/* La navegación del marco clásico. En la 2.1 la puerta es el submenú Agencia;
                acá —que es donde está hoy la mayoría de las agencias— es esta pestaña, más
                el link dentro del tab Portal de cada cliente. */}
            <AgencyTabs activeTab="-eighth"/>

            {renderRank()}

            {/* La voz de la agencia es config a nivel AGENCIA y su audiencia son los clientes del
                portal: vive acá —la única pantalla de agencia sobre el portal— debajo del ranking
                para no empujar la lista accionable. Se muestra también con cero clientes: definir
                la voz ANTES de invitar al primero es el orden natural.

                Va FUERA del ranking, no después de sus early-return: son dos features sin nada en
                común, y colgar la voz del overview significaba que un 404 del gate de plan o un 500
                del ranking la hacían desaparecer. Trae su propio loading / error / reintento. */}
            <AgencyVoiceSettings/>
        </section>
    );
}

export default AgencyPortalUsageContent;
